import { useEffect, useRef, useState } from 'react';
import { Download, Image as ImageIcon, Loader2, Maximize2 } from 'lucide-react';
import { chatApi, type ChatDrawing as Drawing } from '@/services/api';
import { API_CONFIG } from '@/config/api.config';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

/**
 * The drawing made for an answer, shown with it.
 *
 * Display only: the decision was taken before the turn did any work (see
 * ChatDrawingAsk), so by the time this appears the drawing is confirmed, being made, or
 * made. What is left is to wait for it, show it, and let it be saved.
 *
 * The wait is real — an image model takes most of a minute, which is several times what
 * the answer takes — so the answer is released first and this fills in underneath it. The
 * poll is the same endpoint the chat itself polls; the row carries the status and, when it
 * lands, the key of the image.
 *
 * It opens full screen, and that is not a nicety: a patent figure carries reference
 * numerals, and numerals are the thing these models render worst. Whether 12 reads as 12
 * or as 72 cannot be judged in a 420-pixel thumbnail beside an answer, and it is the whole
 * question when the drawing is going to a draftsperson or into a provisional.
 */

/** While a drawing is being made. */
const POLL_MS = 4000;

function imageUrl(key: string): string {
  const token = localStorage.getItem('id_token') || '';
  return `${API_CONFIG.baseUrl}/chat/image/${key}?id_token=${encodeURIComponent(token)}`;
}

async function download(key: string) {
  try {
    const response = await fetch(imageUrl(key));
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = key.split('/').pop() || 'drawing.png';
    document.body.appendChild(anchor);
    anchor.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(anchor);
  } catch (error) {
    console.error('Failed to download the drawing:', error);
  }
}

export default function ChatDrawing({
  messageId,
  drawing,
  onChange,
}: {
  messageId: string;
  drawing: Drawing;
  onChange: (drawing: Drawing) => void;
}) {
  const [viewing, setViewing] = useState(false);
  /**
   * Whether the image is on the page yet.
   *
   * The controls hang over the drawing, so offering them before it has rendered offers
   * them over nothing: hovering the empty box produced an expand button that opened a
   * blank dialog and a download button that saved a failed request. They appear when the
   * browser says it has the image.
   */
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  const timer = useRef<number | null>(null);
  // The drawing as it stands, for the poll to compare against. The interval is not
  // re-subscribed when the status moves from queued to generating — it is still a wait —
  // so the closure would otherwise compare against the state it started with and report
  // a change on every tick, marking the session unsaved each time.
  const latest = useRef(drawing);

  // "confirmed" is the attorney's yes; the turn is still gathering what the drawing is
  // described from, so it reads as part of the same wait.
  const waiting =
    drawing.status === 'confirmed' ||
    drawing.status === 'queued' ||
    drawing.status === 'generating';

  useEffect(() => {
    latest.current = drawing;
  }, [drawing]);

  // Each attempt gets its own key, so a redraw is a different image and has to be waited
  // for again — otherwise the controls stay up over the previous one while the new one
  // loads.
  useEffect(() => {
    setLoaded(false);
    setBroken(false);
  }, [drawing.s3Key]);

  // What the row says, once, when this appears. The copy saved with the session is the
  // drawing as it was when the answer arrived, so one finished after that — or finished
  // while the tab was closed — would otherwise still show as being drawn.
  useEffect(() => {
    if (!waiting) return;
    let dropped = false;
    (async () => {
      try {
        const response = await chatApi.getMessage(messageId);
        const current = response?.image_proposal;
        if (!dropped && current && current.status !== latest.current.status) {
          latest.current = current;
          onChange(current);
        }
      } catch (error) {
        console.error('Failed to read the drawing on this message:', error);
      }
    })();
    return () => {
      dropped = true;
    };
    // Once per message: the poll below carries it from there.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  useEffect(() => {
    if (!waiting) {
      if (timer.current) {
        window.clearInterval(timer.current);
        timer.current = null;
      }
      return;
    }

    timer.current = window.setInterval(async () => {
      try {
        const response = await chatApi.getMessage(messageId);
        const current = response?.image_proposal;
        const known = latest.current;
        if (
          current &&
          (current.status !== known.status ||
            current.s3Key !== known.s3Key ||
            current.failure !== known.failure)
        ) {
          latest.current = current;
          onChange(current);
        }
      } catch (error) {
        console.error('Failed to check on the drawing:', error);
      }
    }, POLL_MS);

    return () => {
      if (timer.current) {
        window.clearInterval(timer.current);
        timer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, messageId]);

  // Turned down, dropped, or never confirmed: nothing to show beside the answer.
  if (
    drawing.status === 'declined' ||
    drawing.status === 'cancelled' ||
    drawing.status === 'asked'
  ) {
    return null;
  }

  return (
    <div className="mx-2 mb-1 mt-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-800">
        <ImageIcon className="h-3.5 w-3.5 text-blue-600" />
        {drawing.summary || 'The drawing'}
      </div>

      {waiting && (
        <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-600">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
          Drawing — about a minute.
        </div>
      )}

      {/* Said out loud, because it changes what to expect of the result: a revision is an
          edit of the earlier drawing and keeps its arrangement, where a new drawing does
          not. The model decides which this is, so only the finished proposal knows. */}
      {(drawing.references?.length ?? 0) > 0 && (
        <p className="mt-1 text-[11px] text-gray-500">
          Revising {drawing.references?.[0]?.caption || 'the earlier drawing'}.
        </p>
      )}

      {drawing.status === 'generated' && drawing.s3Key && !broken && (
        <div className="group relative mt-2 inline-block">
          <img
            src={imageUrl(drawing.s3Key)}
            alt={drawing.summary || 'Generated drawing'}
            onLoad={() => setLoaded(true)}
            onError={() => setBroken(true)}
            onClick={() => loaded && setViewing(true)}
            className={`max-h-[420px] rounded-md border border-gray-200 bg-white ${
              loaded ? 'cursor-zoom-in' : ''
            }`}
            title={loaded ? 'Open the drawing full size' : undefined}
          />
          {/* Only once there is something to act on. */}
          {loaded && (
            <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              <button
                onClick={() => setViewing(true)}
                className="rounded-md bg-black/50 p-1.5 text-white hover:bg-black/70"
                title="Open the drawing full size"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => download(drawing.s3Key as string)}
                className="rounded-md bg-black/50 p-1.5 text-white hover:bg-black/70"
                title="Download the drawing"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Drawn, and then not reachable — an expired session, or a key that no longer
          resolves. Said rather than left as an empty box with a hover state. */}
      {drawing.status === 'generated' && broken && (
        <p className="mt-1.5 text-xs text-gray-500">
          The drawing was made but could not be loaded. Reloading the page usually fixes
          it.
        </p>
      )}

      {drawing.status === 'failed' && (
        <p className="mt-1.5 text-xs text-red-700">
          {drawing.failure || 'The drawing could not be made.'}
        </p>
      )}

      {drawing.status === 'generated' && (
        <p className="mt-1.5 text-[10px] leading-snug text-gray-500">
          A working drawing, not a formal drawing under 37 CFR 1.84. Ask for another to
          change what it shows.
        </p>
      )}

      {/* Full size, because the numerals are what has to be checked and they cannot be
          read in the thumbnail. Scrollable rather than scaled to fit: a tall figure at
          full width is worth scrolling, and shrinking it to the viewport would undo the
          point of opening it. */}
      <Dialog open={viewing} onOpenChange={(open) => !open && setViewing(false)}>
        <DialogContent className="flex max-h-[95vh] w-[95vw] max-w-[1400px] flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6 text-sm">
              <span className="truncate">{drawing.summary || 'The drawing'}</span>
              {drawing.s3Key && (
                <button
                  onClick={() => download(drawing.s3Key as string)}
                  className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-gray-300 px-2 py-0.5 text-xs font-normal text-gray-600 hover:bg-gray-50"
                  title="Download the drawing"
                >
                  <Download className="h-3 w-3" />
                  Download
                </button>
              )}
            </DialogTitle>
          </DialogHeader>
          {drawing.s3Key && (
            <div className="min-h-0 flex-1 overflow-auto bg-white">
              <img
                src={imageUrl(drawing.s3Key)}
                alt={drawing.summary || 'Generated drawing'}
                className="mx-auto h-auto w-full max-w-full"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
